import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosMallaAPI, bomberosPersonalAPI } from '../../lib/api';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Settings,
    BookOpen,
    Users,
    Search,
    Zap,
    GraduationCap,
    Database,
    Loader2,
    Pencil,
    Plus,
    GripVertical,
    X,
    Check,
    Save,
    Trash2,
} from 'lucide-react';

const NIVEL_LABEL = {
    postulante: 'Postulante',
    inicial: 'Inicial',
    operativo: 'Operativo',
    profesional: 'Profesional',
};

const NIVEL_COLOR = {
    postulante: 'bg-gray-700',
    inicial: 'bg-blue-600',
    operativo: 'bg-red-600',
    profesional: 'bg-slate-800',
};

const NIVEL_BORDER = {
    postulante: 'border-gray-400',
    inicial: 'border-blue-400',
    operativo: 'border-red-400',
    profesional: 'border-slate-500',
};

const NIVEL_LIGHT = {
    postulante: 'bg-gray-50 dark:bg-gray-900/50',
    inicial: 'bg-blue-50 dark:bg-blue-950/30',
    operativo: 'bg-red-50 dark:bg-red-950/30',
    profesional: 'bg-slate-50 dark:bg-slate-900/50',
};

const CAT_OPTIONS = [
    { value: 'obligatorio', label: 'Obligatorio' },
    { value: 'complementario', label: 'Complementario' },
    { value: 'especialidad', label: 'Especialidad' },
];

export default function ConfiguradorMallaPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [anio, setAnio] = useState('2025');
    const [tab, setTab] = useState('cursos');
    const [editMode, setEditMode] = useState(false);

    const [mallaData, setMallaData] = useState(null);
    const [bomberos, setBomberos] = useState([]);
    const [searchBombero, setSearchBombero] = useState('');

    // Add/Edit course form
    const [showCursoForm, setShowCursoForm] = useState(false);
    const [cursoForm, setCursoForm] = useState({ id: null, nombre: '', nivel: 'inicial', categoria: 'obligatorio', subcategoria: '' });

    // Drag & drop
    const dragItem = useRef(null);

    useEffect(() => { loadData(); }, []);
    useEffect(() => { loadMalla(); }, [anio]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [mallaRes, bombRes] = await Promise.all([
                bomberosMallaAPI.getCursos(anio),
                bomberosPersonalAPI.getAll({ activo: true }),
            ]);
            setMallaData(mallaRes.data);
            setBomberos(bombRes.data);
        } catch (err) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const loadMalla = async () => {
        try {
            const res = await bomberosMallaAPI.getCursos(anio);
            setMallaData(res.data);
        } catch (err) {
            toast.error('Error al cargar malla');
        }
    };

    const handleCargarMalla = async () => {
        if (!window.confirm('¿Cargar las mallas oficiales 2024 y 2025?\n\nEsto reemplazará los cursos de malla existentes con los datos oficiales del Excel.')) return;
        try {
            setSaving(true);
            const res = await bomberosMallaAPI.cargarOficial();
            toast.success(res.data.message);
            await loadMalla();
        } catch (err) {
            toast.error('Error al cargar malla');
        } finally {
            setSaving(false);
        }
    };

    const handleAsignarAutomatico = async () => {
        if (!window.confirm('¿Asignar malla automáticamente a todos los bomberos según fecha de ingreso?\n\nHasta 31/12/2024 → Malla 2024\nDesde 01/01/2025 → Malla 2025')) return;
        try {
            setSaving(true);
            const res = await bomberosPersonalAPI.asignarMallas();
            toast.success(res.data.message);
            const bombRes = await bomberosPersonalAPI.getAll({ activo: true });
            setBomberos(bombRes.data);
        } catch (err) {
            toast.error('Error al asignar mallas');
        } finally {
            setSaving(false);
        }
    };

    const handleAsignarMallaBombero = async (bomberoId, malla) => {
        try {
            await bomberosPersonalAPI.asignarMalla(bomberoId, malla);
            setBomberos(prev => prev.map(b => b.id === bomberoId ? { ...b, malla_asignada: malla } : b));
            toast.success('Malla actualizada');
        } catch (err) {
            toast.error('Error al actualizar');
        }
    };

    // --- DRAG & DROP ---
    const [dragOverInfo, setDragOverInfo] = useState(null); // {nivel, index}

    const handleDragStart = (curso, nivel) => {
        dragItem.current = { ...curso, sourceNivel: nivel };
    };

    const handleDragOverItem = (e, nivel, index) => {
        e.preventDefault();
        setDragOverInfo({ nivel, index });
    };

    const handleDragOverZone = (e, nivel) => {
        e.preventDefault();
        // Only set if not already over a specific item
        const cursos = niveles[nivel] || [];
        setDragOverInfo({ nivel, index: cursos.length });
    };

    const handleDrop = async (e, targetNivel, targetIndex) => {
        e.preventDefault();
        const item = dragItem.current;
        setDragOverInfo(null);
        if (!item) return;

        const sameNivel = item.sourceNivel === targetNivel;

        if (sameNivel) {
            // Reorder within same level
            const cursos = [...(niveles[targetNivel] || [])];
            const fromIdx = cursos.findIndex(c => c.id === item.id);
            if (fromIdx === -1 || fromIdx === targetIndex) { dragItem.current = null; return; }
            const [moved] = cursos.splice(fromIdx, 1);
            const insertAt = targetIndex > fromIdx ? targetIndex - 1 : targetIndex;
            cursos.splice(insertAt, 0, moved);
            const reorderData = cursos.map((c, i) => ({ id: c.id, orden: i + 1 }));
            try {
                await bomberosMallaAPI.reordenarCursos(reorderData);
                toast.success('Orden actualizado');
                await loadMalla();
            } catch (err) {
                toast.error('Error al reordenar');
            }
        } else {
            // Move to different level + set order at position
            try {
                const targetCursos = [...(niveles[targetNivel] || [])];
                await bomberosMallaAPI.updateCurso(item.id, { nivel: targetNivel, orden: targetIndex + 1 });
                // Reorder remaining in target
                const newTargetList = [...targetCursos];
                newTargetList.splice(targetIndex, 0, { id: item.id });
                const reorderData = newTargetList.map((c, i) => ({ id: c.id, orden: i + 1 }));
                await bomberosMallaAPI.reordenarCursos(reorderData);
                toast.success(`"${item.nombre}" movido a ${NIVEL_LABEL[targetNivel]}`);
                await loadMalla();
            } catch (err) {
                toast.error('Error al mover curso');
            }
        }
        dragItem.current = null;
    };

    // --- CHANGE CATEGORY ---
    const handleChangeCategoria = async (cursoId, newCat) => {
        try {
            const isOblig = newCat === 'obligatorio';
            await bomberosMallaAPI.updateCurso(cursoId, { categoria: newCat, obligatorio: isOblig });
            toast.success('Categoría actualizada');
            await loadMalla();
        } catch (err) {
            toast.error('Error al actualizar categoría');
        }
    };

    // --- EDIT COURSE ---
    const handleOpenEdit = (curso) => {
        setCursoForm({ id: curso.id, nombre: curso.nombre, nivel: curso.nivel || 'inicial', categoria: curso.categoria || 'obligatorio', subcategoria: curso.subcategoria || '' });
        setShowCursoForm(true);
    };

    const handleOpenAdd = () => {
        setCursoForm({ id: null, nombre: '', nivel: 'inicial', categoria: 'obligatorio', subcategoria: '' });
        setShowCursoForm(true);
    };

    // --- DELETE COURSE ---
    const handleDeleteCurso = async (curso) => {
        if (!window.confirm(`¿Eliminar "${curso.nombre}" de la malla ${anio}?\n\nEsto también eliminará el progreso asociado.`)) return;
        try {
            await bomberosMallaAPI.deleteCurso(curso.id);
            toast.success('Curso eliminado');
            await loadMalla();
        } catch (err) {
            toast.error('Error al eliminar');
        }
    };

    // --- SAVE COURSE (create or update) ---
    const handleSaveCurso = async () => {
        if (!cursoForm.nombre.trim()) { toast.error('Ingrese nombre del curso'); return; }
        try {
            setSaving(true);
            if (cursoForm.id) {
                // Update existing
                await bomberosMallaAPI.updateCurso(cursoForm.id, {
                    nombre: cursoForm.nombre.trim(),
                    nivel: cursoForm.nivel,
                    categoria: cursoForm.categoria,
                    subcategoria: cursoForm.categoria === 'especialidad' ? (cursoForm.subcategoria || 'otra') : null,
                    obligatorio: cursoForm.categoria === 'obligatorio',
                });
                toast.success('Curso actualizado');
            } else {
                // Create new
                await bomberosMallaAPI.createCurso({
                    anio,
                    nombre: cursoForm.nombre.trim(),
                    nivel: cursoForm.nivel,
                    categoria: cursoForm.categoria,
                    subcategoria: cursoForm.categoria === 'especialidad' ? (cursoForm.subcategoria || 'otra') : null,
                    obligatorio: cursoForm.categoria === 'obligatorio',
                });
                toast.success('Curso agregado');
            }
            setCursoForm({ id: null, nombre: '', nivel: 'inicial', categoria: 'obligatorio', subcategoria: '' });
            setShowCursoForm(false);
            await loadMalla();
        } catch (err) {
            toast.error('Error al guardar curso');
        } finally {
            setSaving(false);
        }
    };

    const bomberosFiltrados = bomberos.filter(b => {
        if (!searchBombero) return true;
        const term = searchBombero.toLowerCase();
        return b.nombre_completo?.toLowerCase().includes(term)
            || b.rut?.toLowerCase().includes(term)
            || b.compania_nombre?.toLowerCase().includes(term);
    });

    if (loading) {
        return (
            <>
                <BomberosHeader />
                <div className="space-y-4 p-4 md:p-6 pt-20">
                    <Skeleton className="h-10 w-64" />
                    <Skeleton className="h-96 w-full" />
                </div>
            </>
        );
    }

    const niveles = mallaData?.niveles || {};
    const nivelKeys = ['postulante', 'inicial', 'operativo', 'profesional'];
    const activeNiveles = nivelKeys.filter(n => (niveles[n] || []).length > 0);
    const allNiveles = nivelKeys; // Show all in edit mode
    const especKeys = Object.keys(niveles).filter(k => k.startsWith('especialidad_'));
    const compKey = niveles.complementario?.length > 0 ? 'complementario' : null;
    const mallaVacia = (mallaData?.total_cursos || 0) === 0;

    // Flatten all courses for edit mode
    const allCursosFlat = [];
    for (const [key, cursos] of Object.entries(niveles)) {
        for (const c of cursos) {
            allCursosFlat.push({ ...c, seccion: key });
        }
    }

    return (
        <>
        <BomberosHeader />
        <div className="space-y-4 md:space-y-6 p-4 md:p-6 pt-20 md:pt-20 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/malla-curricular')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center gap-2 text-foreground">
                            <Settings className="h-5 w-5 md:h-6 md:w-6 shrink-0" />
                            Configurador de Mallas
                        </h1>
                        <p className="text-xs sm:text-sm text-muted-foreground">Cursos fijos de malla y asignación a bomberos</p>
                    </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/bomberos/malla-curricular')} data-testid="ver-malla-btn">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Ver Malla Visual
                </Button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b pb-0">
                <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'cursos' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setTab('cursos')}
                    data-testid="tab-cursos"
                >
                    <GraduationCap className="h-4 w-4 inline mr-2" />
                    Cursos de Malla
                </button>
                <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'bomberos' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setTab('bomberos')}
                    data-testid="tab-bomberos"
                >
                    <Users className="h-4 w-4 inline mr-2" />
                    Asociar Bomberos
                </button>
            </div>

            {/* ===== TAB: CURSOS DE MALLA ===== */}
            {tab === 'cursos' && (
                <div className="space-y-4">
                    {/* Selector de año + Cargar malla + Edit toggle */}
                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                        <div className="flex gap-3 items-center">
                            <Select value={anio} onValueChange={setAnio}>
                                <SelectTrigger className="w-40" data-testid="select-anio-config">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="2024">Malla 2024</SelectItem>
                                    <SelectItem value="2025">Malla 2025</SelectItem>
                                </SelectContent>
                            </Select>
                            {!mallaVacia && (
                                <Button
                                    variant={editMode ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setEditMode(!editMode)}
                                    data-testid="toggle-edit-mode"
                                >
                                    {editMode ? <><Check className="h-4 w-4 mr-1" /> Listo</> : <><Pencil className="h-4 w-4 mr-1" /> Editar</>}
                                </Button>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {editMode && (
                                <Button variant="outline" size="sm" onClick={handleOpenAdd} data-testid="add-curso-btn">
                                    <Plus className="h-4 w-4 mr-1" /> Agregar Curso
                                </Button>
                            )}
                            <Button onClick={handleCargarMalla} disabled={saving} variant="default" size="sm" data-testid="cargar-malla-btn">
                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                                {saving ? 'Cargando...' : 'Cargar Mallas Oficiales'}
                            </Button>
                        </div>
                    </div>

                    {/* Course form (add/edit) */}
                    {showCursoForm && editMode && (
                        <Card className="border-2 border-dashed border-green-400 bg-green-50/50 dark:bg-green-950/20">
                            <CardContent className="py-4 space-y-3">
                                <p className="text-sm font-semibold flex items-center gap-2 text-foreground">
                                    {cursoForm.id ? <Pencil className="h-4 w-4 text-blue-600" /> : <Plus className="h-4 w-4 text-green-600" />}
                                    {cursoForm.id ? 'Editar Curso' : `Nuevo Curso para Malla ${anio}`}
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    <Input
                                        placeholder="Nombre del curso"
                                        value={cursoForm.nombre}
                                        onChange={(e) => setCursoForm(p => ({ ...p, nombre: e.target.value }))}
                                        data-testid="curso-form-nombre"
                                    />
                                    <Select value={cursoForm.nivel} onValueChange={(v) => setCursoForm(p => ({ ...p, nivel: v }))}>
                                        <SelectTrigger data-testid="curso-form-nivel"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {nivelKeys.map(n => <SelectItem key={n} value={n}>{NIVEL_LABEL[n]}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Select value={cursoForm.categoria} onValueChange={(v) => setCursoForm(p => ({ ...p, categoria: v }))}>
                                        <SelectTrigger data-testid="curso-form-cat"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {CAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    {cursoForm.categoria === 'especialidad' && (
                                        <Input
                                            placeholder="Subcategoría (ej: rescate)"
                                            value={cursoForm.subcategoria}
                                            onChange={(e) => setCursoForm(p => ({ ...p, subcategoria: e.target.value }))}
                                            data-testid="curso-form-subcat"
                                        />
                                    )}
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <Button variant="ghost" size="sm" onClick={() => setShowCursoForm(false)}>Cancelar</Button>
                                    <Button size="sm" onClick={handleSaveCurso} disabled={saving} data-testid="save-curso-form">
                                        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                                        {cursoForm.id ? 'Guardar Cambios' : 'Crear Curso'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {mallaVacia ? (
                        <Card className="border-2 border-dashed">
                            <CardContent className="py-12 text-center">
                                <GraduationCap className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                                <h3 className="text-lg font-semibold mb-2 text-foreground">Malla {anio} sin cursos</h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Presione "Cargar Mallas Oficiales" para importar los cursos de las mallas 2024 y 2025
                                </p>
                                <Button onClick={handleCargarMalla} disabled={saving} data-testid="cargar-malla-empty-btn">
                                    <Database className="h-4 w-4 mr-2" />
                                    Cargar Mallas Oficiales
                                </Button>
                            </CardContent>
                        </Card>
                    ) : editMode ? (
                        /* ===== EDIT MODE: Drag & Drop columns ===== */
                        <>
                            <p className="text-xs text-muted-foreground">Arrastra cursos entre columnas para cambiar su nivel. Usa el selector para cambiar entre Obligatorio/Complementario/Especialidad.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {allNiveles.map(nivel => (
                                    <div
                                        key={nivel}
                                        className={`rounded-lg border-2 ${NIVEL_BORDER[nivel]} min-h-[200px] transition-colors`}
                                        onDragOver={(e) => handleDragOverZone(e, nivel)}
                                        onDrop={(e) => handleDrop(e, nivel, (niveles[nivel] || []).length)}
                                        data-testid={`drop-zone-${nivel}`}
                                    >
                                        <div className={`${NIVEL_COLOR[nivel]} text-white text-center py-2 px-3 rounded-t-md`}>
                                            <span className="text-xs font-bold uppercase tracking-wider">{NIVEL_LABEL[nivel]}</span>
                                            <Badge variant="outline" className="ml-2 text-[10px] border-white/40 text-white/80">
                                                {(niveles[nivel] || []).length}
                                            </Badge>
                                        </div>
                                        <div className={`${NIVEL_LIGHT[nivel]} p-2 space-y-0 min-h-[150px] rounded-b-md`}>
                                            {(niveles[nivel] || []).map((curso, idx) => (
                                                <div key={curso.id}>
                                                    {/* Drop indicator line */}
                                                    <div
                                                        className={`h-1 rounded transition-colors ${dragOverInfo?.nivel === nivel && dragOverInfo?.index === idx ? 'bg-blue-400' : 'bg-transparent'}`}
                                                        onDragOver={(e) => handleDragOverItem(e, nivel, idx)}
                                                        onDrop={(e) => handleDrop(e, nivel, idx)}
                                                    />
                                                    <div
                                                        draggable
                                                        onDragStart={() => handleDragStart(curso, nivel)}
                                                        onDragOver={(e) => handleDragOverItem(e, nivel, idx)}
                                                        onDrop={(e) => handleDrop(e, nivel, idx)}
                                                        className="flex items-center gap-1 p-1.5 bg-card rounded border border-border shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow group"
                                                        data-testid={`drag-curso-${curso.id}`}
                                                    >
                                                        <GripVertical className="h-3 w-3 text-muted-foreground/40 shrink-0 group-hover:text-muted-foreground" />
                                                        <span className="text-[11px] flex-1 leading-tight text-foreground min-w-0 truncate">{curso.nombre}</span>
                                                        <div className="flex items-center gap-0 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button onClick={() => handleOpenEdit(curso)} className="p-1 text-muted-foreground hover:text-blue-600" data-testid={`edit-btn-${curso.id}`}><Pencil className="h-3 w-3" /></button>
                                                            <button onClick={() => handleDeleteCurso(curso)} className="p-1 text-muted-foreground hover:text-red-600" data-testid={`delete-btn-${curso.id}`}><Trash2 className="h-3 w-3" /></button>
                                                        </div>
                                                        <Select
                                                            value={curso.categoria || 'obligatorio'}
                                                            onValueChange={(v) => handleChangeCategoria(curso.id, v)}
                                                        >
                                                            <SelectTrigger className="h-6 w-[80px] text-[9px] px-1 border-border shrink-0">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {CAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                            ))}
                                            {/* Last drop indicator */}
                                            <div
                                                className={`h-1 rounded transition-colors ${dragOverInfo?.nivel === nivel && dragOverInfo?.index === (niveles[nivel] || []).length ? 'bg-blue-400' : 'bg-transparent'}`}
                                                onDragOver={(e) => handleDragOverItem(e, nivel, (niveles[nivel] || []).length)}
                                                onDrop={(e) => handleDrop(e, nivel, (niveles[nivel] || []).length)}
                                            />
                                            {(niveles[nivel] || []).length === 0 && (
                                                <div className="text-center py-6 text-xs text-muted-foreground opacity-60">
                                                    Arrastra cursos aquí
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Complementarios & Especialidades in edit mode */}
                            {(compKey || especKeys.length > 0) && (
                                <Card>
                                    <CardHeader className="py-3">
                                        <CardTitle className="text-sm text-foreground">Complementarios y Especialidades</CardTitle>
                                    </CardHeader>
                                    <CardContent className="pt-0 space-y-3">
                                        {compKey && (
                                            <div>
                                                <p className="text-xs font-bold text-purple-700 dark:text-purple-400 mb-1">Complementarios ({niveles.complementario.length})</p>
                                                <div className="space-y-1">
                                                    {niveles.complementario.map(curso => (
                                                        <div key={curso.id} className="flex items-center gap-1 p-1.5 bg-card rounded border border-border text-[11px] group">
                                                            <span className="flex-1 text-foreground min-w-0 truncate">{curso.nombre}</span>
                                                            <div className="flex items-center gap-0 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button onClick={() => handleOpenEdit(curso)} className="p-1 text-muted-foreground hover:text-blue-600"><Pencil className="h-3 w-3" /></button>
                                                                <button onClick={() => handleDeleteCurso(curso)} className="p-1 text-muted-foreground hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                                                            </div>
                                                            <Select value={curso.categoria} onValueChange={(v) => handleChangeCategoria(curso.id, v)}>
                                                                <SelectTrigger className="h-6 w-[80px] text-[9px] px-1 border-border shrink-0"><SelectValue /></SelectTrigger>
                                                                <SelectContent>
                                                                    {CAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {especKeys.map(k => {
                                            const label = k.replace('especialidad_', '').replace(/_/g, ' ');
                                            return (
                                                <div key={k}>
                                                    <p className="text-xs font-bold text-purple-700 dark:text-purple-400 mb-1 uppercase">{label} ({(niveles[k]||[]).length})</p>
                                                    <div className="space-y-1">
                                                        {(niveles[k]||[]).map(curso => (
                                                            <div key={curso.id} className="flex items-center gap-1 p-1.5 bg-card rounded border border-border text-[11px] group">
                                                                <span className="flex-1 text-foreground min-w-0 truncate">{curso.nombre}</span>
                                                                <div className="flex items-center gap-0 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <button onClick={() => handleOpenEdit(curso)} className="p-1 text-muted-foreground hover:text-blue-600"><Pencil className="h-3 w-3" /></button>
                                                                    <button onClick={() => handleDeleteCurso(curso)} className="p-1 text-muted-foreground hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                                                                </div>
                                                                <Select value={curso.categoria} onValueChange={(v) => handleChangeCategoria(curso.id, v)}>
                                                                    <SelectTrigger className="h-6 w-[80px] text-[9px] px-1 border-border shrink-0"><SelectValue /></SelectTrigger>
                                                                    <SelectContent>
                                                                        {CAT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    ) : (
                        /* ===== VIEW MODE (original) ===== */
                        <>
                            {/* Resumen */}
                            <div className="flex gap-3 text-sm flex-wrap">
                                <Badge variant="outline" className="text-foreground">{mallaData?.total_cursos} cursos totales</Badge>
                                <Badge className="bg-green-100 text-green-700">{mallaData?.total_obligatorios} obligatorios</Badge>
                                <Badge className="bg-purple-100 text-purple-700">{(mallaData?.total_cursos || 0) - (mallaData?.total_obligatorios || 0)} opcionales</Badge>
                            </div>

                            {/* Niveles obligatorios */}
                            {activeNiveles.map(nivel => (
                                <Card key={nivel}>
                                    <CardHeader className="py-3">
                                        <CardTitle className="text-sm flex items-center gap-2 text-foreground">
                                            <span className={`w-3 h-3 rounded ${NIVEL_COLOR[nivel]}`}></span>
                                            Nivel {NIVEL_LABEL[nivel]}
                                            <Badge variant="outline" className="text-xs ml-auto">{(niveles[nivel] || []).length} cursos</Badge>
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="pt-0">
                                        <div className="space-y-1">
                                            {(niveles[nivel] || []).map((curso, idx) => (
                                                <div key={curso.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted text-sm">
                                                    <span className="text-xs text-muted-foreground w-5 text-right shrink-0">{idx + 1}.</span>
                                                    <span className="flex-1 text-foreground">{curso.nombre}</span>
                                                    <Badge variant="outline" className={curso.obligatorio ? 'border-green-500 text-green-700 text-[10px]' : 'text-muted-foreground text-[10px]'}>
                                                        {curso.obligatorio ? 'Obligatorio' : curso.categoria === 'especialidad' ? 'Especialidad' : 'Complementario'}
                                                    </Badge>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}

                            {/* Complementarios */}
                            {compKey && (
                                <Card>
                                    <CardHeader className="py-3">
                                        <CardTitle className="text-sm flex items-center gap-2 text-foreground">
                                            <span className="w-3 h-3 rounded bg-purple-700"></span>
                                            Cursos Complementarios
                                            <Badge variant="outline" className="text-xs ml-auto">{niveles.complementario.length} cursos</Badge>
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="pt-0 space-y-1">
                                        {niveles.complementario.map((curso, idx) => (
                                            <div key={curso.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted text-sm">
                                                <span className="text-xs text-muted-foreground w-5 text-right shrink-0">{idx + 1}.</span>
                                                <span className="flex-1 text-foreground">{curso.nombre}</span>
                                                <Badge variant="outline" className="text-muted-foreground text-[10px]">Complementario</Badge>
                                            </div>
                                        ))}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Especialidades */}
                            {especKeys.length > 0 && (
                                <Card>
                                    <CardHeader className="py-3">
                                        <CardTitle className="text-sm flex items-center gap-2 text-foreground">
                                            <span className="w-3 h-3 rounded bg-purple-700"></span>
                                            Especialidades
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="pt-0 space-y-4">
                                        {especKeys.map(k => {
                                            const label = k.replace('especialidad_', '').replace(/_/g, ' ');
                                            return (
                                                <div key={k}>
                                                    <p className="text-xs font-bold text-purple-700 mb-1 uppercase tracking-wide">{label}</p>
                                                    <div className="space-y-1">
                                                        {(niveles[k] || []).map((curso, idx) => (
                                                            <div key={curso.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted text-sm">
                                                                <span className="text-xs text-muted-foreground w-5 text-right shrink-0">{idx + 1}.</span>
                                                                <span className="flex-1 text-foreground">{curso.nombre}</span>
                                                                <Badge variant="outline" className="text-muted-foreground text-[10px]">Especialidad</Badge>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ===== TAB: BOMBEROS ===== */}
            {tab === 'bomberos' && (
                <div className="space-y-4">
                    <Card className="border-2 border-dashed border-blue-300 bg-blue-50/50">
                        <CardContent className="py-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div>
                                    <p className="font-medium text-sm flex items-center gap-2 text-foreground">
                                        <Zap className="h-4 w-4 text-blue-600" />
                                        Asignación Automática
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Ingreso hasta 31/12/2024 → Malla 2024 | Desde 01/01/2025 → Malla 2025
                                    </p>
                                </div>
                                <Button variant="default" size="sm" onClick={handleAsignarAutomatico} disabled={saving} data-testid="asignar-auto-btn">
                                    <Zap className="h-4 w-4 mr-2" />
                                    {saving ? 'Asignando...' : 'Asignar por Fecha de Ingreso'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Buscar bombero por nombre, RUT o compañía..."
                            value={searchBombero}
                            onChange={(e) => setSearchBombero(e.target.value)}
                            className="pl-10"
                            data-testid="search-bombero-config"
                        />
                    </div>

                    <div className="flex gap-3 text-sm">
                        <Badge variant="outline" className="border-amber-500 text-amber-700">
                            Malla 2024: {bomberos.filter(b => b.malla_asignada === '2024').length}
                        </Badge>
                        <Badge variant="outline" className="border-amber-500 text-amber-700">
                            Malla 2025: {bomberos.filter(b => b.malla_asignada === '2025').length}
                        </Badge>
                        <Badge variant="outline" className="text-muted-foreground">
                            Sin malla: {bomberos.filter(b => !b.malla_asignada).length}
                        </Badge>
                    </div>

                    <Card>
                        <CardContent className="p-0">
                            <div className="max-h-[60vh] overflow-y-auto divide-y">
                                {bomberosFiltrados.map(bombero => (
                                    <div key={bombero.id} className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate text-foreground">{bombero.nombre_completo}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {bombero.rut} | {bombero.compania_nombre || 'Sin compañía'} | Ingreso: {bombero.fecha_ingreso || 'Sin fecha'}
                                            </p>
                                        </div>
                                        <Select
                                            value={bombero.malla_asignada || 'none'}
                                            onValueChange={(v) => handleAsignarMallaBombero(bombero.id, v === 'none' ? null : v)}
                                        >
                                            <SelectTrigger className="w-32 text-xs" data-testid={`malla-select-${bombero.rut}`}>
                                                <SelectValue placeholder="Sin malla" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Sin malla</SelectItem>
                                                <SelectItem value="2024">Malla 2024</SelectItem>
                                                <SelectItem value="2025">Malla 2025</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                ))}
                                {bomberosFiltrados.length === 0 && (
                                    <div className="py-12 text-center text-muted-foreground">
                                        <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>{searchBombero ? 'No se encontraron bomberos' : 'No hay bomberos activos'}</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
        </>
    );
}
